import { BonusAttrSymbol } from '../../../../../../types/bonus';

export const EOF = '\0';

export enum TokenType {
	EOF = 'EOF',
	HASHTAG = 'HASHTAG',
	MENTION = 'MENTION',
	AMOUNT = 'AMOUNT',
	TEXT = 'TEXT',
	USER_TAG = 'USER_TAG',
	EMOJI = 'EMOJI',
	NEWLINE = 'NEWLINE',
}

export class Token {
	constructor(
		public text: string,
		public kind: TokenType,
		public start: number,
		public end: number
	) {}
}

/*
Lexes bonus text into a stream of tokens with information about where they occurred in the document
 */
export default class BonusLexer {
	public curChar = '';
	private curPos = -1;
	private hasAmount = false;
	private insideLink = false;
	private openParenthesisCount = 0;

	constructor(
		private source: string,
		private options = { checkForAmount: true, checkForEmoji: true }
	) {
		this.nextChar();
	}

	private nextChar() {
		this.curPos += 1;

		// NOTE: tracking open parenthesis count for links and image links
		if (this.curChar === '(') {
			this.openParenthesisCount += 1;
		}

		if (this.curChar === ')') {
			this.openParenthesisCount -= 1;

			// NOTE: after all are closed we are no longer inside a link
			if (this.openParenthesisCount === 0) {
				this.insideLink = false;
			}
		}

		if (this.curPos >= this.source.length) {
			this.insideLink = false;
			this.curChar = EOF;
		} else {
			this.curChar = this.source[this.curPos];
		}
	}

	// peek ahead at the next character or pass in the number of characters to peek ahead to
	private peek(count = 1) {
		if (this.curPos + count >= this.source.length) {
			return EOF;
		}
		return this.source[this.curPos + count];
	}

	private prevChar() {
		if (this.curPos < 1) {
			return '';
		}
		return this.source[this.curPos - 1];
	}

	private tokenStartRegex() {
		return this.hasAmount || !this.options.checkForAmount ? /[#@&~]/ : /[#@+&~]/;
	}

	private isNextCharTokenStart() {
		const peekRegex = this.tokenStartRegex();
		const isLink = this.peek() === '[' && this.curChar !== '!';
		const isImage = this.peek() === '!' && this.peek(2) === '[';

		return (this.options.checkForEmoji && this.peek() === BonusAttrSymbol.Emoji && this.curChar === ' ') || isLink || isImage || this.peek().match(peekRegex);
	}

	getToken() {
		let token = null;
		const isLink = this.curChar === BonusAttrSymbol.Link && this.prevChar() !== '!';
		const isImage = this.curChar === '!' && this.peek() === '[';

		// NOTE: pull out links and images first because if they have characters like & or # in them they will be tokenized incorrectly
		if (isImage || isLink) {
			this.insideLink = true;
			const startPos = this.curPos;

			// keep stepping through characters while inside the link
			// links are formatted as [text](url) and images are ![text](url)
			// see nextChar definition for parenthesis count logic where insideLink is set to false when all parenthesis are closed
			while (this.insideLink) {
				this.nextChar();
			}

			const tokenText = this.source.substring(startPos, this.curPos + 1);

			token = new Token(tokenText, TokenType.TEXT, startPos, this.curPos);
		} else if (this.curChar === BonusAttrSymbol.Recipient) {
			const startPos = this.curPos;

			// keep stepping through characters while the next character is a word character or a period
			while (this.peek().match(/[\w.'@+-]/)) {
				this.nextChar();
			}
			const tokenText = this.source.substring(startPos, this.curPos + 1);

			token = new Token(tokenText, TokenType.MENTION, startPos, this.curPos);
		} else if (this.curChar === BonusAttrSymbol.UserTag) {
			const startPos = this.curPos;

			// keep stepping through characters while the next character is a word character or a period
			while (this.peek().match(/[\w.'@+-]/)) {
				this.nextChar();
			}
			const tokenText = this.source.substring(startPos, this.curPos + 1);

			token = new Token(tokenText, TokenType.USER_TAG, startPos, this.curPos);
		} else if (this.curChar === BonusAttrSymbol.Hashtag) {
			const startPos = this.curPos;

			// keep stepping through characters while the next character is a word character or a dash
			while (this.peek().match(/[\w-]/)) {
				this.nextChar();
			}
			const tokenText = this.source.substring(startPos, this.curPos + 1);

			token = new Token(tokenText, TokenType.HASHTAG, startPos, this.curPos);
		} else if (this.options.checkForAmount && this.curChar === BonusAttrSymbol.Amount && !this.hasAmount) {
			const startPos = this.curPos;

			// keep stepping through characters while the next character is a word character
			while (this.peek().match(/\w/)) {
				this.nextChar();
			}

			const tokenText = this.source.substring(startPos, this.curPos + 1);

			const containsLetters = tokenText.match(/[0-9]*[a-zA-Z]+/);
			const isMissingAmount = startPos === this.curPos;

			const tokenType = containsLetters || isMissingAmount ? TokenType.TEXT : TokenType.AMOUNT;

			if (tokenType === TokenType.AMOUNT) {
				this.hasAmount = true;
			}

			token = new Token(tokenText, tokenType, startPos, this.curPos);
		} else if (this.options.checkForEmoji && this.curChar === BonusAttrSymbol.Emoji) {
			token = new Token(this.curChar, TokenType.EMOJI, this.curPos, this.curPos);
		} else if (this.curChar === EOF) {
			token = new Token('', TokenType.EOF, this.curPos, this.curPos);
		} else if (this.curChar === '\n') {
			token = new Token(this.curChar, TokenType.NEWLINE, this.curPos, this.curPos);
		} else {
			const startPos = this.curPos;

			while (this.peek() !== EOF && !this.isNextCharTokenStart()) {
				this.nextChar();
			}
			const tokenText = this.source.substring(startPos, this.curPos + 1);

			token = new Token(tokenText, TokenType.TEXT, startPos, this.curPos);
		}

		this.nextChar();
		return token;
	}
}
